import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Info } from "lucide-react";
import {
  useVitalsHome,
  usePatientSymptoms,
  useDeletePatientSymptom,
  usePatientInfo,
} from "@/hooks/useConsultation";
import { AddVitalsDialog } from "@/components/consultation/AddVitalsDialog";
import { AddSymptomsDialog } from "@/components/consultation/AddSymptomsDialog";
import { InvestigationNotesCard } from "@/components/consultation/cards/InvestigationNotesCard";
import { DiagnosisCard } from "@/components/consultation/cards/DiagnosisCard";
import { VitalsMetricCard } from "@/components/consultation/cards/VitalsMetricCard";
import { MetricChartModal } from "@/components/MetricChartModal";
import type { HealthMetric } from "@/types/tracker";
import {
  formatBloodPressureRange,
  formatVitalRange,
  hasVitalData,
  deriveChartUser,
} from "@/lib/vitals-utils";
import { EmptyState } from "@/components/consultation/EmptyState";
import { groupSymptomsByType } from "@/lib/symptoms";

interface PrescriptionObservationsProps {
  appointmentId: string;
  patientId: string;
  isCompleted?: boolean;
}

export const PrescriptionObservations = ({
  appointmentId,
  patientId,
  isCompleted = false,
}: PrescriptionObservationsProps) => {
  const [isAddVitalsOpen, setIsAddVitalsOpen] = useState(false);
  const [isAddSymptomsOpen, setIsAddSymptomsOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<HealthMetric | null>(
    null
  );
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);

  const { data: vitalsData } = useVitalsHome(appointmentId);
  const { data: patientSymptoms = [] } = usePatientSymptoms(appointmentId);
  const { data: patientInfo } = usePatientInfo(patientId, appointmentId);
  const deleteSymptom = useDeletePatientSymptom();

  // Symptom drafts use activity_status; inactive items stay in the API
  // response but must be hidden until publish.
  const activeSymptoms = useMemo(
    () => patientSymptoms.filter((s) => s.activity_status === "active"),
    [patientSymptoms]
  );

  // Drop empty buckets so the layout collapses gracefully when a category has
  // no symptoms recorded for this appointment.
  const symptomGroups = useMemo(
    () => groupSymptomsByType(activeSymptoms).filter((g) => g.items.length > 0),
    [activeSymptoms]
  );

  const chartUser = useMemo(() => deriveChartUser(patientInfo), [patientInfo]);

  const openChart = (
    metricId: string,
    title: string,
    value: string,
    unit: string,
    category: any
  ) => {
    setSelectedMetric({
      id: metricId,
      title,
      value,
      unit,
      period: "Last 7 days",
      category,
    });
    setIsChartModalOpen(true);
  };

  const vitalsCards = useMemo(() => {
    const cards = [];
    if (vitalsData?.blood_pressure && hasVitalData(vitalsData.blood_pressure)) {
      cards.push({
        title: "Blood Pressure",
        value: formatBloodPressureRange(vitalsData.blood_pressure),
        unit: "mmHg",
        periodLabel: "Last 7 Days",
        onClick: () =>
          openChart(
            "blood-pressure",
            "Blood Pressure",
            formatBloodPressureRange(vitalsData.blood_pressure),
            "mmHg",
            "heart-health"
          ),
      });
    }
    if (vitalsData?.heart_rate && hasVitalData(vitalsData.heart_rate)) {
      cards.push({
        title: "Heart Rate",
        value: formatVitalRange(
          vitalsData.heart_rate.min,
          vitalsData.heart_rate.max
        ),
        unit: "bpm",
        periodLabel: "Last 24 Hours",
        onClick: () =>
          openChart(
            "heart-rate",
            "Heart Rate",
            formatVitalRange(
              vitalsData.heart_rate.min,
              vitalsData.heart_rate.max
            ),
            "bpm",
            "heart-health"
          ),
      });
    }
    if (vitalsData?.blood_glucose && hasVitalData(vitalsData.blood_glucose)) {
      cards.push({
        title: "Blood Glucose",
        value: formatVitalRange(
          vitalsData.blood_glucose.min,
          vitalsData.blood_glucose.max
        ),
        unit: "mg/dL",
        periodLabel: "Last 24 Hours",
        onClick: () =>
          openChart(
            "blood-glucose",
            "Blood Glucose",
            formatVitalRange(
              vitalsData.blood_glucose.min,
              vitalsData.blood_glucose.max
            ),
            "mg/dL",
            "diabetes"
          ),
      });
    }
    if (vitalsData?.temperature && hasVitalData(vitalsData.temperature)) {
      cards.push({
        title: "Temperature",
        value: formatVitalRange(
          vitalsData.temperature.min,
          vitalsData.temperature.max
        ),
        unit: "°F",
        periodLabel: "Last 7 Days",
        onClick: () =>
          openChart(
            "temperature",
            "Temperature",
            formatVitalRange(
              vitalsData.temperature.min,
              vitalsData.temperature.max
            ),
            "°F",
            "all"
          ),
      });
    }
    if (
      vitalsData?.respiratory_rate &&
      hasVitalData(vitalsData.respiratory_rate)
    ) {
      cards.push({
        title: "Resp. Rate",
        value: formatVitalRange(
          vitalsData.respiratory_rate.min,
          vitalsData.respiratory_rate.max
        ),
        unit: "rpm",
        periodLabel: "Last 7 Days",
        onClick: () =>
          openChart(
            "respiratory-rate",
            "Respiratory Rate",
            formatVitalRange(
              vitalsData.respiratory_rate.min,
              vitalsData.respiratory_rate.max
            ),
            "rpm",
            "respiratory-rate"
          ),
      });
    }
    return cards;
  }, [vitalsData]);

  const handleDeleteSymptom = async (symptomId: string) => {
    try {
      await deleteSymptom.mutateAsync({ symptomId, appointmentId });
    } catch (error) {
      console.error("Failed to delete symptom", error);
    }
  };

  return (
    <div className="p-4 space-y-4 h-full overflow-y-auto">
      {/* Vitals Card */}
      <Card className="rounded-3xl border-border/60 bg-card shadow-sm">
        <CardHeader className="p-4 border-b border-border/60 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="z-h3 text-foreground">Vitals</CardTitle>
            <Info className="size-4 text-muted-foreground" />
          </div>
          {!isCompleted && (
            <Button
              size="icon"
              className="h-9 w-9 rounded-xl"
              onClick={() => setIsAddVitalsOpen(true)}
            >
              <Plus className="size-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-4">
          {vitalsCards.length > 0 ? (
            <div className="bg-muted/30 rounded-xl p-3">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
                {vitalsCards.map((card, index) => (
                  <VitalsMetricCard
                    key={index}
                    title={card.title}
                    value={card.value}
                    unit={card.unit}
                    periodLabel={card.periodLabel}
                    onClick={card.onClick}
                  />
                ))}
              </div>
            </div>
          ) : (
            <EmptyState title="No vitals data available" variant="compact" />
          )}
        </CardContent>
      </Card>

      {/* Symptoms Card */}
      <Card className="rounded-3xl border-border/60 bg-card shadow-sm">
        <CardHeader className="p-4 border-b border-border/60 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="z-h3 text-foreground">
              Symptoms Observed
            </CardTitle>
            <Info className="size-4 text-muted-foreground" />
          </div>
          {!isCompleted && (
            <Button
              size="icon"
              className="h-9 w-9 rounded-xl"
              onClick={() => setIsAddSymptomsOpen(true)}
            >
              <Plus className="size-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-4">
          {symptomGroups.length > 0 ? (
            <div className="space-y-2">
              {symptomGroups.map((group) => (
                <div
                  key={group.key}
                  className="rounded-xl border border-border/50 bg-muted/20 p-3 space-y-1.5"
                >
                  <p className="z-label text-muted-foreground">{group.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {group.items.map((symptom) => (
                      <Badge
                        key={symptom.id}
                        variant="secondary"
                        className="h-7 px-3 z-caption gap-2 transition-colors rounded-full"
                      >
                        <span className="truncate">
                          {symptom.symptoms?.name ??
                            symptom.custom_symptoms ??
                            "Custom symptom"}
                        </span>
                        {!isCompleted && (
                          <button
                            onClick={() => handleDeleteSymptom(symptom.id)}
                            className="bg-destructive/20 hover:bg-destructive/30 rounded-full p-0.5 transition-colors shrink-0"
                          >
                            <X className="size-3 text-destructive" />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No symptoms recorded" variant="compact" />
          )}
        </CardContent>
      </Card>

      {/* Bottom Row: Investigation Notes and Diagnosis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="h-[400px]">
          <InvestigationNotesCard
            appointmentId={appointmentId}
            isCompleted={isCompleted}
          />
        </div>
        <div className="h-[400px]">
          <DiagnosisCard
            appointmentId={appointmentId}
            patientId={patientId}
            isCompleted={isCompleted}
          />
        </div>
      </div>

      {/* Dialogs */}
      <AddVitalsDialog
        open={isAddVitalsOpen}
        onOpenChange={setIsAddVitalsOpen}
        appointmentId={appointmentId}
        patientId={patientId}
      />

      <AddSymptomsDialog
        open={isAddSymptomsOpen}
        onOpenChange={setIsAddSymptomsOpen}
        appointmentId={appointmentId}
      />

      <MetricChartModal
        isOpen={isChartModalOpen}
        onClose={() => setIsChartModalOpen(false)}
        metric={selectedMetric}
        selectedPatient={chartUser}
        isCompleted={isCompleted}
      />
    </div>
  );
};
